import { useRef, useEffect, useState } from "react";
import { TextField } from "@radix-ui/themes";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

export function QRScanner<T>({
  onScanned,
  schema,
}: {
  onScanned?: (value: T) => void;
  schema?: z.AnyZodObject;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState("");

  const parseQR = (value: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      try {
        const parsedQR = JSON.parse(value);

        if (schema) {
          const validatedQR = schema.parse(parsedQR);
          resolve(validatedQR);
        } else {
          resolve(parsedQR);
        }
      } catch (err) {
        toast.error(
          "Cannot parse scanned image. Please check if it's valid QR code"
        );
        setValue("");
        reject(err);
      }
    });
  };

  const onScan = async () => {
    if (value) {
      const parsedValue = await parseQR(value);
      onScanned?.(parsedValue);
      setValue("");
    }
  };

  useEffect(() => {
    const onKeyPress = function (e: KeyboardEvent) {
      if (["Enter", "Escape"].includes(e.key)) return;

      if ((e.target as HTMLElement)?.tagName !== "INPUT") {
        inputRef.current?.focus();
        setValue(e.key);
        e.preventDefault();
      }
    };

    document.addEventListener("keypress", onKeyPress);

    return () => document.removeEventListener("keypress", onKeyPress);
  }, []);

  return (
    <TextField.Root
      ref={inputRef}
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Scan QR code"
      inputMode="none"
      className={twMerge([
        import.meta.env.PROD && "!h-0 overflow-hidden opacity-0",
      ])}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onScan();
        }
      }}
    ></TextField.Root>
  );
}
